import React, { useState, useEffect } from 'react';
import { Plus, Copy, Trash2, Link2, Users, Clock, CheckCircle2, Mail, Send, Loader2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { useInvites } from '../../../hooks/useDatabase';
import { useAuthStore } from '../../../store/authStore';
import { formatDate, getDaysUntil } from '../../../lib/dateUtils';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { sendBatchInvites } from '../../../lib/email';

export default function InvitesPage() {
  const { user } = useAuthStore();
  const { createInvite, loading } = useInvites();
  const [invites, setInvites] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ role: 'employee', department: 'any', maxUses: 10, expiryDays: 30, supervisorId: '' });
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [inviteEmails, setInviteEmails] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => { loadInvites(); loadSupervisors(); }, []);

  const loadInvites = async () => {
    const data = await db.invites.toArray();
    setInvites(data);
  };

  const loadSupervisors = async () => {
    const users = await db.users.toArray();
    setSupervisors(users.filter(u => u.role === 'supervisor' && u.status === 'active'));
  };

  const activeInvites = invites.filter(i => i.status === 'active');

  const copyToClipboard = (code) => {
    const url = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied to clipboard');
  };

  const handleCreateInvite = async () => {
    if (!newInvite.role) {
      toast.error('Please select a role');
      return;
    }
    if (newInvite.role === 'employee' && !newInvite.supervisorId) {
      toast.error('Please select a supervisor for the employee');
      return;
    }
    try {
      const invite = await createInvite({
        role: newInvite.role,
        department: newInvite.department,
        maxUses: newInvite.maxUses,
        expiryDate: Date.now() + newInvite.expiryDays * 24 * 60 * 60 * 1000,
        createdBy: user.id,
        supervisorId: newInvite.role === 'employee' ? newInvite.supervisorId : null
      });
      setGeneratedInvite(invite);
      setInviteEmails(''); // Reset emails
      toast.success('Invite created successfully!');
      loadInvites();
    } catch (error) {
      toast.error('Failed to create invite');
    }
  };

  const handleSendEmails = async () => {
    if (!inviteEmails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    const emails = inviteEmails
      .split(/[\n,]/) // Split by newline or comma
      .map(e => e.trim())
      .filter(e => e); // Remove empty strings

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return;
    }

    // Ensure max uses covers the number of emails
    if (generatedInvite.maxUses < emails.length) {
      toast.warning(`Warning: You are sending to ${emails.length} people but the invite only has ${generatedInvite.maxUses} uses.`);
      // We allow it but warn, or we could block. Allowing for now.
    }

    setSendingEmails(true);
    try {
      const inviteLink = `${window.location.origin}/register?code=${generatedInvite.inviteCode}`;
      await sendBatchInvites(emails, inviteLink);
      toast.success(`Sent invites to ${emails.length} recipients!`);
      setInviteEmails(''); // Clear functionality
    } catch (error) {
      toast.error('Failed to send emails. Please try again.');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleDeactivateInvite = async (id) => {
    await db.invites.update(id, { status: 'expired' });
    toast.success('Invite deactivated');
    loadInvites();
  };

  const handleDeleteInvite = async (id) => {
    await db.invites.delete(id);
    toast.success('Invite deleted');
    loadInvites();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invite Links</h1>
          <p className="text-gray-500 mt-1">Manage employee registration invites</p>
        </div>
        <Button onClick={() => { setShowCreateModal(true); setGeneratedInvite(null); }} className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Create Invite
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-500">Active Invites</p><p className="text-3xl font-bold text-gray-900">{activeInvites.length}</p></div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center"><Link2 className="w-6 h-6 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-500">Total Uses</p><p className="text-3xl font-bold text-gray-900">{invites.reduce((sum, i) => sum + i.usedCount, 0)}</p></div>
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-violet-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-500">Expired</p><p className="text-3xl font-bold text-gray-900">{invites.filter(i => i.status !== 'active').length}</p></div>
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-gray-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Active Invites</CardTitle></CardHeader>
        <CardContent>
          {activeInvites.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Invite Code</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Department</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Usage</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Expires</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeInvites.map(invite => (
                    <tr key={invite.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4"><code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">{invite.inviteCode}</code></td>
                      <td className="py-3 px-4"><Badge className={invite.role === 'supervisor' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'}>{invite.role}</Badge></td>
                      <td className="py-3 px-4 text-sm text-gray-600">{invite.department || 'Any'}</td>
                      <td className="py-3 px-4"><span className="text-sm"><span className="font-medium">{invite.usedCount}</span><span className="text-gray-400"> / {invite.maxUses}</span></span></td>
                      <td className="py-3 px-4"><span className="text-sm text-gray-600">{formatDate(invite.expiryDate)}<span className="text-gray-400 ml-1">({getDaysUntil(invite.expiryDate)}d)</span></span></td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => copyToClipboard(invite.inviteCode)}><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-600" onClick={() => handleDeleteInvite(invite.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8"><Link2 className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No active invites</p></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Invite Link</DialogTitle>
            <DialogDescription>Generate a new registration invite</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {generatedInvite ? (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center relative">
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6 text-emerald-600 hover:bg-emerald-100" onClick={() => { setGeneratedInvite(null); setShowCreateModal(false); }}>
                    <X className="w-4 h-4" />
                  </Button>
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Invite code generated!</p>
                  <code className="text-lg font-mono font-bold text-emerald-700">{generatedInvite.inviteCode}</code>
                </div>

                <div className="space-y-3 pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Send via Email</Label>
                    <Badge variant="outline" className="text-xs font-normal">Optional</Badge>
                  </div>
                  <Textarea
                    placeholder="Enter email addresses"
                    value={inviteEmails}
                    onChange={(e) => setInviteEmails(e.target.value)}
                    className="min-h-[100px] font-sans"
                  />
                  <Button
                    onClick={handleSendEmails}
                    disabled={sendingEmails || !inviteEmails.trim()}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {sendingEmails ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Send Invites
                      </>
                    )}
                  </Button>
                </div>

                <div className="space-y-2 pt-2 border-t border-gray-100">
                  <Label>Or copy link manually</Label>
                  <div className="flex gap-2">
                    <Input value={`${window.location.origin}/register?code=${generatedInvite.inviteCode}`} readOnly className="font-mono text-sm bg-gray-50" />
                    <Button variant="outline" onClick={() => copyToClipboard(generatedInvite.inviteCode)}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Select value={newInvite.role} onValueChange={(v) => setNewInvite({ ...newInvite, role: v, supervisorId: '' })}>
                    <SelectTrigger><SelectValue placeholder="Select role..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newInvite.role === 'employee' && (
                  <div className="space-y-2">
                    <Label>Assign Supervisor *</Label>
                    <Select value={newInvite.supervisorId} onValueChange={(v) => setNewInvite({ ...newInvite, supervisorId: v })}>
                      <SelectTrigger><SelectValue placeholder="Select supervisor..." /></SelectTrigger>
                      <SelectContent>
                        {supervisors.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.profile?.firstName} {s.profile?.lastName} - {s.profile?.department}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Department (Optional)</Label>
                  <Select value={newInvite.department} onValueChange={(v) => setNewInvite({ ...newInvite, department: v })}>
                    <SelectTrigger><SelectValue placeholder="Any department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Department</SelectItem>
                      <SelectItem value="Engineering">Engineering</SelectItem>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="HR">HR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Max Uses</Label><Input type="number" value={newInvite.maxUses} onChange={(e) => setNewInvite({ ...newInvite, maxUses: parseInt(e.target.value) || 1 })} min={1} /></div>
                  <div className="space-y-2"><Label>Expires In (days)</Label><Input type="number" value={newInvite.expiryDays} onChange={(e) => setNewInvite({ ...newInvite, expiryDays: parseInt(e.target.value) || 7 })} min={1} /></div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>{generatedInvite ? 'Close' : 'Cancel'}</Button>
            {!generatedInvite && (<Button onClick={handleCreateInvite} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">{loading ? 'Creating...' : 'Generate Invite'}</Button>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
